import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { VolumeX, Flag } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { emitChatRead } from '../lib/unreadBus';
import ChatUI from '../components/ChatUI';
import ReportModal from '../components/ReportModal';

export default function DM() {
  const { id: threadId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);

  const other = thread?.other_user;

  // Viewport height now handled by CSS (100dvh + body.chat-locked + viewport meta interactive-widget=resizes-content)

  const fetchThread = useCallback(async () => {
    try {
      const { data } = await api.get(`/dm/threads/${threadId}`);
      setThread(data);
    } catch { navigate(-1); }
  }, [threadId, navigate]);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/dm/threads/${threadId}/messages`);
      setMessages(data);
      setLoading(false);
    } catch { setLoading(false); }
  }, [threadId]);

  useEffect(() => { fetchThread(); }, [fetchThread]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(() => { fetchMessages(); fetchThread(); }, 4000);
    return () => clearInterval(interval);
  }, [fetchMessages, fetchThread]);

  const onMarkRead = useCallback(async () => {
    try {
      await api.put(`/dm/threads/${threadId}/read`);
      emitChatRead({ type: 'dm', id: threadId });
    } catch { /* swallow — badge will refresh on next poll */ }
  }, [threadId]);

  const onSend = async (text, replyTo, extras) => {
    const body = { text };
    if (replyTo) body.reply_to = replyTo;
    if (extras?.media_url) { body.media_url = extras.media_url; body.media_type = extras.media_type || 'image'; }
    if (extras?.location) body.location = extras.location;
    if (extras?.audio_url) { body.audio_url = extras.audio_url; body.audio_duration = extras.audio_duration || 0; }
    const { data } = await api.post(`/dm/threads/${threadId}/messages`, body);
    setMessages(prev => [...prev, data]);
  };

  const handleMute = async () => {
    if (!other) return;
    try { await api.post(`/users/${other.id}/mute`); } catch {}
    navigate('/activity');
  };

  const menuItems = useMemo(() => [
    {
      key: 'mute',
      label: 'Mute & Block',
      desc: 'Stop receiving messages from this user',
      icon: VolumeX,
      onClick: handleMute,
    },
    {
      key: 'report',
      label: 'Report User',
      desc: 'Flag behavior that breaks our guidelines',
      icon: Flag,
      destructive: true,
      onClick: () => setShowReport(true),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [other?.id]);

  return (
    <>
      <ChatUI
        title={other?.display_name || 'Direct Message'}
        subtitle={other ? `@${other.username}` : ''}
        avatar={other?.profile_photo}
        onBack={() => navigate(-1)}
        headerOnClick={() => other?.id && navigate(`/user/${other.id}`)}
        messages={messages}
        currentUserId={currentUser?.id}
        isDM={true}
        seenAt={thread?.other_last_read || ''}
        onSend={onSend}
        onMarkRead={onMarkRead}
        menuItems={menuItems}
        loading={loading}
      />
      {showReport && other && (
        <ReportModal type="user" targetId={other.id} onClose={() => setShowReport(false)} />
      )}
    </>
  );
}
